import React, { useState, useEffect, useMemo } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { listClientDocs, storagePathFromUrl, findUploadedFilePath } from '../../storage';
import {
  stripDocExtension,
  humanizeStorageFileName,
  isTaskStorageRelativePath,
} from '../../core/helpers';
import { resolveDocTemplate } from '../../core/seed-doc-templates';
import { DocActions } from '../../components/DocActions';
import { Icon, InlineIcon } from '../../components/Icon';

function storageRelativePath(companyId, file) {
  const raw = file?.path || file?.name || "";
  if (!raw) return "";
  const prefix = `${companyId}/`;
  return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
}

function resolveDocStoragePath(doc, files, task, dirIdx, slotId) {
  if (doc?.path) return doc.path;
  const fromUrl = storagePathFromUrl(doc?.url);
  if (fromUrl) return fromUrl;
  const fromUpload = findUploadedFilePath(files, task?.clientId, task?.id, dirIdx, slotId);
  if (fromUpload) return fromUpload;
  const hint = stripDocExtension(doc?.name || '').toLowerCase();
  if (!hint) return '';
  const hit = (files || []).find((f) => {
    const base = stripDocExtension(humanizeStorageFileName(f.name || f.path || '')).toLowerCase();
    return base === hint || base.includes(hint) || hint.includes(base);
  });
  return hit?.path || '';
}

function buildTaskDocuments(submissions, myTasks, docTpls, storageFiles) {
  const rows = [];
  const seen = new Set();

  submissions.forEach(sub => {
    const task = myTasks.find(t => t.id === sub.taskId);
    if (!task) return;
    const tpl = resolveDocTemplate(task.docTemplateId, docTpls);

    Object.entries(sub.documents || {}).forEach(([dirIdx, slots]) => {
      Object.entries(slots || {}).forEach(([slotId, doc]) => {
        if (!doc?.path && !doc?.name) return;
        const path = resolveDocStoragePath(doc, storageFiles, task, dirIdx, slotId);
        if (path && seen.has(path)) return;
        if (path) seen.add(path);

        const slot = tpl?.docSlots?.find(s => s.id === slotId);
        const displayName = stripDocExtension(
          doc.name || slot?.label || humanizeStorageFileName(path)
        );

        rows.push({
          key: path || `${sub.id}-${dirIdx}-${slotId}`,
          path,
          displayName,
          taskTitle: task.title || "",
          taskId: task.id,
          companyId: task.clientId,
          dirIdx,
          slotId,
          uploadedByLabel: "Uploaded by Client",
          uploadedBy: "client",
          size: doc.size,
          date: sub.submittedAt,
          doc: { ...doc, path, name: doc.name || displayName },
        });
      });
    });
  });

  return rows;
}

function buildOtherDocuments(files, companyId, seenPaths) {
  const rows = [];

  (files || []).forEach(f => {
    const rel = storageRelativePath(companyId, f);
    if (!rel || isTaskStorageRelativePath(rel)) return;

    const fullPath = f.path || `${companyId}/${rel}`;
    if (seenPaths.has(fullPath)) return;

    const isTeam = rel.startsWith("team/");
    const filePart = isTeam ? rel.slice(5) : rel;
    const displayName = stripDocExtension(humanizeStorageFileName(filePart));

    rows.push({
      key: fullPath,
      path: fullPath,
      displayName,
      taskTitle: "",
      uploadedByLabel: isTeam ? "Uploaded by Team" : "Uploaded by Client",
      uploadedBy: isTeam ? "team" : "client",
      size: f.size,
      date: f.created_at,
      doc: { path: fullPath, name: displayName },
    });
  });

  return rows;
}

function ClientDocs() {
  const { submissions, tasks, myCompanies, visibleCompanyIds, showToast, docTpls, notifFocus, setNotifFocus } = useApp();
  const [tab, setTab] = useState("tasks");

  useEffect(() => {
    if (!notifFocus?.docsTab) return;
    setTab(notifFocus.docsTab === "team" ? "mine" : "tasks");
    setNotifFocus(null);
  }, [notifFocus?.docsTab, setNotifFocus]);
  const [files, setFiles] = useState([]);

  const companyId = visibleCompanyIds[0] || myCompanies[0]?.id;
  const prefix = companyId ? `${companyId}/` : "";
  const myTasks = tasks.filter(t => visibleCompanyIds.includes(t.clientId));

  const taskDocs = useMemo(
    () => buildTaskDocuments(submissions, myTasks, docTpls, files),
    [submissions, myTasks, docTpls, files]
  );

  const seenTaskPaths = useMemo(() => new Set(taskDocs.map(d => d.path).filter(Boolean)), [taskDocs]);

  const otherDocs = useMemo(
    () => buildOtherDocuments(files, companyId, seenTaskPaths),
    [files, companyId, seenTaskPaths]
  );

  const teamDocs = useMemo(
    () => otherDocs.filter((d) => d.uploadedBy === "team"),
    [otherDocs]
  );

  const loadFiles = async () => {
    if (!prefix) { setFiles([]); return; }
    try {
      setFiles(await listClientDocs(prefix));
    } catch {
      setFiles([]);
    }
  };

  useEffect(() => { loadFiles(); }, [companyId]);

  const DocRow = ({ doc }) => (
    <div className="row-item" style={{ padding: "12px 20px" }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: doc.uploadedBy === "team" ? "rgba(11,31,58,.07)" : "var(--gold-lt)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon name="doc" size={19} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>{doc.displayName}</div>
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3, lineHeight: 1.45 }}>
          <span style={{
            fontWeight: 700,
            color: doc.uploadedBy === "team" ? "var(--navy)" : "#059669",
          }}>
            {doc.uploadedByLabel}
          </span>
          {doc.taskTitle && <> · {doc.taskTitle}</>}
          {doc.size && <> · {typeof doc.size === "number" ? `${doc.size} B` : doc.size}</>}
          {doc.date && <> · {typeof doc.date === "string" && !doc.date.includes("T")
            ? doc.date
            : new Date(doc.date).toLocaleDateString("en-IN")}</>}
        </div>
      </div>
      <DocActions
        doc={doc.doc}
        name={doc.displayName}
        storagePath={doc.path}
        listPrefix={prefix}
        companyId={companyId}
        taskId={doc.taskId}
        dirIdx={doc.dirIdx}
        slotId={doc.slotId}
        files={files}
        onError={(msg) => showToast(msg, "error")}
      />
    </div>
  );

  if (!companyId) {
    return (
      <div className="card">
        <div className="empty">
          <div>No company linked to your account yet.</div>
          <div style={{ fontSize: 12, marginTop: 8 }}>Ask your team to link your login to a client company, then upload documents here.</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="tabs">
        <div className={`tab ${tab === "tasks" ? "on" : ""}`} onClick={() => setTab("tasks")}>
          <InlineIcon name="folderOpen" size={14} gap={5}>From Clients ({taskDocs.length})</InlineIcon>
        </div>
        <div className={`tab ${tab === "mine" ? "on" : ""}`} onClick={() => setTab("mine")}>
          <InlineIcon name="upload" size={14} gap={5}>From Team ({teamDocs.length})</InlineIcon>
        </div>
      </div>

      {tab === "tasks" && (
        <div className="card">
          <div className="card-head">
            <div className="card-title">From Clients</div>
          </div>
          {taskDocs.length === 0
            ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="folder" size={28} /></div>
                <div>No task documents yet</div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Documents you submit on tasks appear here with their field names (e.g. PAN Card).</div>
              </div>
            )
            : taskDocs.map(d => <DocRow key={d.key} doc={d} />)}
        </div>
      )}

      {tab === "mine" && (
        <div className="card">
          <div className="card-head">
            <div className="card-title">From Team</div>
          </div>
          {teamDocs.length === 0
            ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="upload" size={28} /></div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>No team uploads yet</div>
                <div style={{ fontSize: 13 }}>
                  Files your team uploads for you appear here.
                </div>
              </div>
            )
            : teamDocs.map(d => <DocRow key={d.key} doc={d} />)}
        </div>
      )}
    </>
  );
}

export { ClientDocs };
